import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';

// Styled Components
const Container = styled.div`
  padding: clamp(1rem, 3vw, 2rem);
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 1.8rem;
  font-weight: 600;
`;

const CreateButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #0056b3;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border-left: 4px solid #dc3545;
`;

const FormModal = styled.div`
  background: ${props => props.$isDarkMode ? '#2d2d2d' : 'white'};
  padding: 2rem;
  border-radius: 12px;
  box-shadow: ${props => props.$isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'};
  margin-bottom: 2rem;
  border: ${props => props.$isDarkMode ? '1px solid #404040' : '1px solid #e9ecef'};
`;

const FormTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  font-size: 1.3rem;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))'};
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#555'};
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: stretch;
    
    > button {
      flex: 1;
      min-width: 120px;
    }
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #1e7e34;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.$isDarkMode ? '#555555' : '#f8f9fa'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#6c757d'};
  border: ${props => props.$isDarkMode ? '1px solid #404040' : '1px solid #ddd'};
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isDarkMode ? '#444444' : '#e9ecef'};
  }
`;

const AccountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(350px, 100%), 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AccountCard = styled.div`
  background: ${props => props.$isDarkMode ? '#2d2d2d' : 'white'};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${props => props.$isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  border: 2px solid ${props => 
    props.selected 
      ? (props.$isDarkMode ? '#4dabf7' : '#007bff')
      : (props.$isDarkMode ? '#404040' : 'transparent')
  };
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$isDarkMode ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.12)'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.accountType === 'CHECKING' ? 
      'linear-gradient(90deg, #007bff, #0056b3)' : 
      'linear-gradient(90deg, #28a745, #1e7e34)'};
  }
`;

const AccountHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const AccountNumber = styled.div`
  margin: 1.5rem 0 1rem 0;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  font-size: 1.3rem;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 2px;
  text-align: center;
  padding: 0.75rem;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : '#f8f9fa'};
  border-radius: 8px;
  border: ${props => props.$isDarkMode ? '1px solid #404040' : '1px solid #e9ecef'};
`;

const AccountTypeBadge = styled.span`
  background: ${props => props.type === 'CHECKING' ? 
    'linear-gradient(135deg, #007bff, #0056b3)' : 
    'linear-gradient(135deg, #28a745, #1e7e34)'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.active ? 
    'linear-gradient(135deg, #28a745, #1e7e34)' : 
    'linear-gradient(135deg, #dc3545, #c82333)'};
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
`;

const OwnerInfo = styled.div`
  background: ${props => props.$isDarkMode ? '#1e1e1e' : '#f8f9fa'};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border-left: ${props => props.$isDarkMode ? '4px solid #4dabf7' : '4px solid #007bff'};
`;

const OwnerName = styled.div`
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  margin-bottom: 0.25rem;
`;

const OwnerEmail = styled.div`
  color: ${props => props.$isDarkMode ? '#b3b3b3' : '#666'};
  font-size: 0.9rem;
`;

const Balance = styled.div`
  margin-bottom: 1rem;
  text-align: center;
`;

const BalanceLabel = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const BalanceAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  margin: 1rem 0;
`;

const CreatedDate = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const AccountDetails = styled.div`
  background: ${props => props.$isDarkMode ? 
    'linear-gradient(135deg, #1e2a3a, #2d3f5f)' : 
    'linear-gradient(135deg, #e3f2fd, #bbdefb)'};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border-left: ${props => props.$isDarkMode ? '4px solid #4dabf7' : '4px solid #2196f3'};
`;

const DetailsTitle = styled.h5`
  margin: 0 0 0.75rem 0;
  color: ${props => props.$isDarkMode ? '#4dabf7' : '#1976d2'};
  font-weight: 600;
`;

const DetailItem = styled.p`
  margin: 0.25rem 0;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  font-size: 0.9rem;

  strong {
    color: ${props => props.$isDarkMode ? '#4dabf7' : '#1976d2'};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    > button {
      width: 100%;
    }
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  ${props => {
    if (props.variant === 'details') {
      return `
        background: ${props.active ? '#6c757d' : '#17a2b8'};
        color: white;
        &:hover {
          background: ${props.active ? '#5a6268' : '#138496'};
        }
      `;
    }
    if (props.variant === 'delete') {
      return `
        background: #dc3545;
        color: white;
        &:hover {
          background: #c82333;
        }
      `;
    }
  }}
`;

const EmptyState = styled.div`
  background: ${props => props.$isDarkMode ? '#2d2d2d' : 'white'};
  padding: 3rem;
  border-radius: 12px;
  box-shadow: ${props => props.$isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  text-align: center;
  color: ${props => props.$isDarkMode ? '#b3b3b3' : '#6c757d'};
  border: ${props => props.$isDarkMode ? '1px solid #404040' : 'none'};
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#495057'};
`;

const EmptyStateText = styled.p`
  margin: 0;
  color: ${props => props.$isDarkMode ? '#b3b3b3' : '#6c757d'};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #666;
`;

const AccountsPage = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [accountBalance, setAccountBalance] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    userId: '',
    accountType: 'CHECKING',
    initialBalance: 0
  });

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPPORT';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load accounts based on user role
      let accountsResponse;
      if (isAdmin) {
        // Admins see all accounts
        accountsResponse = await apiService.getAccounts();
        // Also load users for admin to create accounts
        const usersResponse = await apiService.getUsers();
        setUsers(usersResponse.users || []);
      } else {
        // Regular users see only their accounts
        accountsResponse = await apiService.getUserAccounts(user.id);
      }
      
      setAccounts(accountsResponse.accounts || []);
    } catch (err) {
      setError('Failed to load accounts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const accountData = {
        userId: formData.userId || user.id, // Use current user if not admin
        accountType: formData.accountType,
        // Only include initialBalance if user is admin
        ...(isAdmin && { initialBalance: parseFloat(formData.initialBalance) || 0 })
      };
      
      await apiService.createAccount(accountData);
      setShowCreateForm(false);
      setFormData({ userId: '', accountType: 'CHECKING', initialBalance: 0 });
      loadData();
    } catch (err) {
      setError('Failed to create account: ' + err.message);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      try {
        await apiService.deleteAccount(accountId);
        loadData();
      } catch (err) {
        setError('Failed to delete account: ' + err.message);
      }
    }
  };

  const loadAccountBalance = async (accountId) => {
    try {
      if (selectedAccountId === accountId) {
        setSelectedAccountId(null);
        setAccountBalance(null);
        return;
      }

      setSelectedAccountId(accountId);
      const response = await apiService.getAccountBalance(accountId);
      setAccountBalance(response);
    } catch (err) {
      setError('Failed to load balance: ' + err.message);
      setSelectedAccountId(null);
      setAccountBalance(null);
    }
  };

  const getUserName = (account) => {
    if (account.first_name && account.last_name) {
      return `${account.first_name} ${account.last_name}`;
    }
    return account.email || 'Unknown User';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div style={{
        padding: 'clamp(1rem, 3vw, 2rem)',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '200px',
          fontSize: '1.1rem',
          color: isDarkMode ? '#b3b3b3' : '#64748b'
        }}>
          Loading accounts...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: 'clamp(1rem, 3vw, 2rem)',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        gap: '1rem',
        flexDirection: 'column',
        alignItems: 'stretch',
        '@media (min-width: 769px)': {
          flexDirection: 'row',
          alignItems: 'center'
        }
      }}>
        <h2 style={{
          margin: 0,
          color: isDarkMode ? '#ffffff' : '#333',
          fontSize: '1.8rem',
          fontWeight: '600'
        }}>
          {isAdmin ? 'All Accounts' : 'My Accounts'}
        </h2>
        <button 
          onClick={() => setShowCreateForm(true)}
          data-testid="create-account-button"
          style={{
            padding: '0.75rem 1.5rem',
            background: isDarkMode ? '#4dabf7' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = isDarkMode ? '#339af0' : '#0056b3'}
          onMouseLeave={(e) => e.target.style.background = isDarkMode ? '#4dabf7' : '#007bff'}
        >
          Create New Account
        </button>
      </div>

      {error && (
        <div 
          data-testid="error-message"
          style={{
            background: isDarkMode ? '#3a1e1e' : '#fee',
            color: isDarkMode ? '#ff6b6b' : '#c33',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            borderLeft: `4px solid ${isDarkMode ? '#ff6b6b' : '#dc3545'}`
          }}>
          {error}
        </div>
      )}

      {/* Create Account Form */}
      {showCreateForm && (
        <FormModal $isDarkMode={isDarkMode}>
          <FormTitle $isDarkMode={isDarkMode}>Create New Account</FormTitle>
          <form onSubmit={handleCreateAccount} data-testid="create-account-form">
            <FormGrid columns={isAdmin && users.length > 0 ? 
              (isAdmin ? '2fr 1fr 1fr' : '1fr 1fr') : '1fr 1fr'}>
              
              {/* User selection for admins */}
              {users.length > 0 && isAdmin && (
                <FormGroup>
                  <Label $isDarkMode={isDarkMode}>Select User</Label>
                  <Select
                    $isDarkMode={isDarkMode}
                    data-testid="user-select"
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    required
                  >
                    <option value="">Choose a user...</option>
                    {users.map((userData) => (
                      <option key={userData.id} value={userData.id}>
                        {userData.first_name} {userData.last_name} ({userData.email})
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              )}

              <FormGroup>
                <Label $isDarkMode={isDarkMode}>Account Type</Label>
                <Select
                  $isDarkMode={isDarkMode}
                  data-testid="account-type-select"
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                >
                  <option value="CHECKING">Checking Account</option>
                  <option value="SAVINGS">Savings Account</option>
                </Select>
              </FormGroup>

              {/* Initial Balance - Only for Admins */}
              {isAdmin && (
                <FormGroup>
                  <Label $isDarkMode={isDarkMode}>Initial Balance</Label>
                  <Input
                    $isDarkMode={isDarkMode}
                    data-testid="initial-balance-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.initialBalance}
                    onChange={(e) => setFormData({ ...formData, initialBalance: e.target.value })}
                    placeholder="0.00"
                  />
                </FormGroup>
              )}
            </FormGrid>

            <ButtonGroup>
              <SubmitButton type="submit" data-testid="submit-create-account">
                Create Account
              </SubmitButton>
              <CancelButton
                $isDarkMode={isDarkMode}
                type="button"
                data-testid="cancel-create-account"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ userId: '', accountType: 'CHECKING', initialBalance: 0 });
                }}
              >
                Cancel
              </CancelButton>
            </ButtonGroup>
          </form>
        </FormModal>
      )}

      {/* Accounts Grid */}
      <AccountsGrid data-testid="accounts-grid">
        {accounts.map((account) => (
          <AccountCard
            key={account.id}
            data-testid={`account-card-${account.id}`}
            $isDarkMode={isDarkMode}
            selected={selectedAccountId === account.id}
            accountType={account.account_type}
          >
            <AccountHeader>
              <AccountTypeBadge type={account.account_type}>
                {account.account_type === 'CHECKING' ? 'Checking' : 'Savings'}
              </AccountTypeBadge>
            </AccountHeader>

            {/* Show user info for admins */}
            {isAdmin && (
              <OwnerInfo $isDarkMode={isDarkMode}>
                <OwnerName $isDarkMode={isDarkMode}>{getUserName(account)}</OwnerName>
                <OwnerEmail $isDarkMode={isDarkMode}>{account.email}</OwnerEmail>
              </OwnerInfo>
            )}

            <Balance>
              <BalanceAmount $isDarkMode={isDarkMode}>{formatCurrency(account.balance)}</BalanceAmount>
            </Balance>

            {/* Account Balance Detail */}
            {selectedAccountId === account.id && accountBalance && (
              <AccountDetails $isDarkMode={isDarkMode}>
                <DetailsTitle $isDarkMode={isDarkMode}>Account Details</DetailsTitle>
                <DetailItem $isDarkMode={isDarkMode}>
                  <strong>Account ID:</strong> {accountBalance.accountId}
                </DetailItem>
                <DetailItem $isDarkMode={isDarkMode}>
                  <strong>Current Balance:</strong> {formatCurrency(accountBalance.balance)}
                </DetailItem>
                <DetailItem $isDarkMode={isDarkMode}>
                  <strong>Created:</strong> {new Date(account.created_at).toLocaleDateString()}
                </DetailItem>
              </AccountDetails>
            )}

            {/* Account Number - Bank Card Style */}
            <AccountNumber $isDarkMode={isDarkMode}>{account.account_number}</AccountNumber>

            <ActionButtons>
              <ActionButton
                variant="details"
                data-testid={`details-button-${account.id}`}
                active={selectedAccountId === account.id}
                onClick={() => loadAccountBalance(account.id)}
              >
                {selectedAccountId === account.id ? 'Hide Details' : 'Show Details'}
              </ActionButton>

              {isAdmin && (
                <ActionButton
                  variant="delete"
                  data-testid={`delete-button-${account.id}`}
                  onClick={() => handleDeleteAccount(account.id)}
                >
                  Delete
                </ActionButton>
              )}
            </ActionButtons>
          </AccountCard>
        ))}
      </AccountsGrid>

      {accounts.length === 0 && (
        <EmptyState $isDarkMode={isDarkMode} data-testid="no-accounts-message">
          <EmptyStateTitle $isDarkMode={isDarkMode}>No accounts found</EmptyStateTitle>
          <EmptyStateText $isDarkMode={isDarkMode}>
            {isAdmin ? 
              'No accounts have been created yet. Create the first account to get started!' :
              'You don\'t have any accounts yet. Create your first account to start banking!'
            }
          </EmptyStateText>
        </EmptyState>
      )}
    </div>
  );
};

export default AccountsPage; 